import getWindow from 'get-window';
import * as React from 'react';
import ReactDOM from 'react-dom';

import {
    type Document,
    isBlockRangeSelection,
    type SelectionRange,
    type Value,
} from '@gitbook/slate';
import { IS_FIREFOX } from '@gitbook/slate-dev-environment';
import { reportException } from '@gitbook/util-debug';
import { useSafeLayoutEffect } from '@gitbook/util-react-hooks';

import type { EditorContainer } from '../interfaces';
import {
    findDOMClosest,
    findDOMRange,
    findRange,
    NODE_TYPE,
    scrollToBlockRange,
    scrollToSelection,
} from '../utils';

const FIREFOX_NODE_TYPE_ACCESS_ERROR = /Permission denied to access property "nodeType"/;

/**
 * Wrap event handlers in `flushControlled`. In sync mode, this is
 * effectively a no-op. In async mode, this ensures all updates scheduled
 * inside the handler are flushed before React yields to the browser.
 */
const flushControlled: (fn: () => void) => void = ReactDOM.flushSync
    ? ReactDOM.flushSync
    : (fn: () => void) => {
          fn();
      };

/**
 * Event handlers used by Slate plugins.
 */
const EVENT_HANDLERS = [
    'onBlur',
    'onClick',
    'onContextMenu',
    'onCompositionEnd',
    'onCompositionStart',
    'onCompositionUpdate',
    'onCopy',
    'onCut',
    'onDragEnd',
    'onDragEnter',
    'onDragExit',
    'onDragLeave',
    'onDragOver',
    'onDragStart',
    'onDrop',
    'onInput',
    'onFocus',
    'onKeyDown',
    'onKeyUp',
    'onPaste',
    'onMouseDown',
] as const;

/**
 * Shortcuts that need to be propagated out of a fragment editor.
 */
const PROPAGATED_SHORTCUTS = ['/', ' ', '<'];

/**
 * Handle editing events in a DOM element
 */
export function useEventHandlers(props: {
    domRef: React.MutableRefObject<HTMLDivElement | null>;
    editor: EditorContainer;
    fragmentEditor?: boolean;
}) {
    const { domRef, editor, fragmentEditor } = props;

    const onEvent = React.useCallback(
        (handler: (typeof EVENT_HANDLERS)[number], event: React.SyntheticEvent<HTMLElement>) => {
            // Some events emanating from fragment editors shouldn't be propagated
            // to the main editor.
            if (
                fragmentEditor &&
                !(
                    handler === 'onDragEnd' ||
                    handler === 'onDragEnter' ||
                    handler === 'onDragExit' ||
                    handler === 'onDragLeave' ||
                    handler === 'onDragOver' ||
                    handler === 'onDragStart' ||
                    handler === 'onDrop' ||
                    // propagate shortcut events
                    (handler === 'onKeyDown' &&
                        event.nativeEvent instanceof KeyboardEvent &&
                        (event.nativeEvent.metaKey ||
                            event.nativeEvent.ctrlKey ||
                            /**
                             * These keys are used as shortcuts in the editor and should be allowed to propagate.
                             */
                            PROPAGATED_SHORTCUTS.includes(event.nativeEvent.key)))
                ) &&
                !editor.readOnly
            ) {
                event.stopPropagation();
            }

            flushControlled(() => {
                const element = domRef.current;

                if (!element) {
                    return;
                }

                // Ignore `onBlur`, `onFocus` and `onSelect` events generated
                // programmatically while updating selection.
                if (
                    editor.isUpdatingSelection.current &&
                    (handler === 'onBlur' || handler === 'onFocus')
                ) {
                    return;
                }

                // Don't handle drag and drop events coming from embedded editors.
                if (
                    handler === 'onDragEnd' ||
                    handler === 'onDragEnter' ||
                    handler === 'onDragExit' ||
                    handler === 'onDragLeave' ||
                    handler === 'onDragOver' ||
                    handler === 'onDragStart' ||
                    handler === 'onDrop'
                ) {
                    // @ts-expect-error
                    const targetEditorNode = findDOMClosest(event.target, '[data-slate-editor]');
                    if (targetEditorNode !== element) {
                        return;
                    }
                }

                // Some events require being in editable in the editor, so if the event
                // target isn't, ignore them.
                if (
                    (handler === 'onBlur' ||
                        handler === 'onCut' ||
                        handler === 'onCompositionEnd' ||
                        handler === 'onCompositionStart' ||
                        handler === 'onCompositionUpdate' ||
                        handler === 'onFocus' ||
                        handler === 'onClick' ||
                        handler === 'onMouseDown' ||
                        handler === 'onInput' ||
                        handler === 'onKeyDown' ||
                        handler === 'onKeyUp' ||
                        handler === 'onPaste') &&
                    !isInEditor(element, event.target)
                ) {
                    return;
                }

                // Same for the following events, except they work in read mode too.
                if (handler === 'onCopy' && !isInEditor(element, event.target, false)) {
                    return;
                }

                editor.change((change) => {
                    editor.stack.run(handler, event, change, editor);
                });
            });
        },
        [editor, domRef, fragmentEditor]
    );

    /*
     * When the editor first mounts in the DOM we need to:
     *
     *   - Add native DOM event listeners.
     */
    useSafeLayoutEffect(() => {
        const element = domRef.current;
        if (!element) {
            return;
        }

        /*
         * On native `selectionchange` event, trigger the `onSelect` handler. This is
         * needed to account for React's `onSelect` being non-standard and not firing
         * until after a selection has been released. This causes issues in situations
         * where another change happens while a selection is being made.
         */
        const onNativeSelectionChange = (event: Event) => {
            const element = domRef.current;
            if (editor.readOnly || !element || editor.isUpdatingSelection.current) {
                return;
            }

            flushControlled(() => {
                const window = getWindow(event.target);
                const { activeElement } = window.document;
                if (activeElement !== element) {
                    return;
                }

                // COMPAT: There are situations where a select event will fire with a new
                // native selection that resolves to the same internal position. In those
                // cases we don't need to trigger any changes, since our internal model is
                // already up to date, but we do want to update the native selection again
                // to make sure it is in sync. (2017/10/16)
                const native = window.getSelection();
                const range = findRange(native, editor.value);
                if (range?.equals(editor.value.selection)) {
                    editor.isUpdatingSelection.current = true;
                    updateSelection(element, editor, { scrollToSelection: false });
                    editor.isUpdatingSelection.current = false;
                    return;
                }
                editor.change((change) => {
                    editor.stack.run('onSelect', event, change, editor);
                });
            });
        };

        /*
         * We use the native `beforeinput` because React uses a custom one
         * https://github.com/facebook/react/issues/11211
         */
        const onNativeBeforeInput = (event: InputEvent) => {
            // Some IMEs/Chrome extensions like e.g. Grammarly set the selection immediately before
            // triggering a `beforeinput` expecting the change to be applied to the immediately before
            // set selection.
            flushControlled(() => {
                const element = domRef.current;

                if (editor.readOnly || !element) {
                    return;
                }

                if (!event.target || !isInEditor(element, event.target)) {
                    return;
                }

                const isCompositionChange =
                    event.inputType === 'insertCompositionText' ||
                    event.inputType === 'deleteCompositionText';

                // COMPAT: use composition change events as a hint to where we should insert
                // composition text if we aren't composing to work around https://github.com/ianstormtaylor/slate/issues/5038
                if (isCompositionChange && editor.isComposing) {
                    return;
                }

                editor.change((change) => {
                    // COMPAT: For the deleting forward/backward input types we don't want
                    // to change the selection because it is the range that will be deleted,
                    // and those commands determine that for themselves.
                    if (
                        !event.inputType.startsWith('delete') ||
                        event.inputType.startsWith('deleteBy')
                    ) {
                        const [targetRange] = event.getTargetRanges();
                        if (targetRange) {
                            const inputRange = findRange(targetRange, editor.value);
                            // @ts-expect-error
                            if (inputRange?.hasMoved(editor.value.selection)) {
                                // We select the current text being transform by this operation to ensure
                                // all plugins can apply at the right place.
                                change.select(inputRange.normalizeSelection(editor.value.document));
                            }
                        }
                    }

                    editor.stack.run('onBeforeInput', event, change, editor);
                });
            });
        };

        const window = getWindow(element);

        window.document.addEventListener('selectionchange', onNativeSelectionChange);
        element.addEventListener('beforeinput', onNativeBeforeInput);

        return () => {
            window.document.removeEventListener('selectionchange', onNativeSelectionChange);
            element.removeEventListener('beforeinput', onNativeBeforeInput);
        };
    }, [editor, onEvent]);

    /*
     * Create the event handlers to be passed to the DOM element.
     */
    return React.useMemo(() => {
        const eventHandlers = {};
        EVENT_HANDLERS.forEach((handler) => {
            eventHandlers[handler] = (event: React.SyntheticEvent<HTMLElement>) => {
                onEvent(handler, event);
            };
        });

        return eventHandlers;
    }, [onEvent]);
}

/**
 * Update the native DOM selection to reflect the internal model.
 */
export function updateSelection(
    element: HTMLElement,
    editor: EditorContainer,
    options: {
        scrollToSelection?: boolean;
    } = {}
): void {
    const { value } = editor;
    const { selection } = value;
    const window = getWindow(element);
    const native = window.getSelection();
    const { activeElement } = window.document;
    // COMPAT: In Firefox, there's a but where `getSelection` can return `null`.
    // https://bugzilla.mozilla.org/show_bug.cgi?id=827585 (2018/11/07)
    if (!native) {
        return;
    }

    // If the Slate selection is blurred, but the DOM's active element is still
    // the editor, we need to blur it.
    if (selection.isBlurred && activeElement === element) {
        editor.isUpdatingSelection.current = true;
        element.blur();
        editor.isUpdatingSelection.current = false;
    }

    // If the Slate selection is unset or a block range, but the DOM selection has a range
    // selected in the editor, we need to remove the range.
    if (selection.isUnset && native.rangeCount && isInEditor(element, native.anchorNode)) {
        editor.isUpdatingSelection.current = true;
        native.removeAllRanges();
        editor.isUpdatingSelection.current = false;
    }

    // If the Slate selection is focused, but the DOM's active element is not
    // the editor, we need to focus it. We prevent scrolling because we handle
    // scrolling to the correct selection.
    if (
        selection.isFocused &&
        activeElement !== element &&
        !activeElement.matches('[data-slate-editor="true"]') // don't change focus if the active element is already a slate editor (e.g. fragment editor)
    ) {
        editor.isUpdatingSelection.current = true;
        element.focus({ preventScroll: true });
        editor.isUpdatingSelection.current = false;
    }

    // Update block range if focused
    if (selection.isFocused && isBlockRangeSelection(selection)) {
        editor.isUpdatingSelection.current = true;
        // If we are in block range selection mode,
        // we need to set the range to the block selection ref.
        // so we can still trigger clipboard events like copy/cut.
        if (editor.blockSelectionRef?.current) {
            const range = document.createRange();
            range.selectNode(editor.blockSelectionRef.current);
            native.removeAllRanges();
            native.addRange(range);
            native.collapseToStart();
        }

        if (options.scrollToSelection !== false) {
            // Scroll to the selection, in case it's out of view.
            scrollToBlockRange(selection);
        }

        editor.isUpdatingSelection.current = false;

        return; // block range selection handled and we don't want the hidden native range being set again.
    }

    // Otherwise, figure out which DOM nodes should be selected...
    if (selection.isFocused && selection.isSet) {
        const current = native.rangeCount ? native.getRangeAt(0) : null;
        const range = findDOMRange(selection, window);

        if (!range) {
            reportRangeError(value);

            return;
        }

        const { startContainer, startOffset, endContainer, endOffset } = range;

        // If the new range matches the current selection, there is nothing to fix.
        // COMPAT: The native `Range` object always has it's "start" first and "end"
        // last in the DOM. It has no concept of "backwards/forwards", so we have
        // to check both orientations here. (2017/10/31)
        if (current) {
            if (
                (startContainer === current.startContainer &&
                    startOffset === current.startOffset &&
                    endContainer === current.endContainer &&
                    endOffset === current.endOffset) ||
                (startContainer === current.endContainer &&
                    startOffset === current.endOffset &&
                    endContainer === current.startContainer &&
                    endOffset === current.startOffset)
            ) {
                return;
            }
        }

        // Otherwise, set the `isUpdatingSelection` flag and update the selection.
        editor.isUpdatingSelection.current = true;

        // COMPAT: Since the DOM range has no concept of backwards/forwards
        // we need to check and do the right thing here.
        // @ts-expect-error
        if (selection.isBackward) {
            native.setBaseAndExtent(
                range.endContainer,
                range.endOffset,
                range.startContainer,
                range.startOffset
            );
        } else {
            native.setBaseAndExtent(
                range.startContainer,
                range.startOffset,
                range.endContainer,
                range.endOffset
            );
        }

        // Scroll to the selection if needed and if the selection
        // is not in a nested editor.
        if (options.scrollToSelection !== false && isInEditor(element, native.anchorNode)) {
            scrollToSelection(native);
        }

        // Then unset the `isUpdatingSelection` flag after a delay, to ensure that
        // it is still set when selection-related events from updating it fire.
        setTimeout(() => {
            editor.isUpdatingSelection.current = false;
        });
    }
}

/*
 * Check if an event `target` is fired from within the contenteditable
 * element. This should be false for edits happening in non-contenteditable
 * children, such as void nodes and other nested Slate editors.
 */
function isInEditor(element: HTMLElement, target: EventTarget, mustBeEditable = true): boolean {
    let el: HTMLElement;

    try {
        // COMPAT: Text nodes don't have `isContentEditable` property. So, when
        // `target` is a text node use its parent node for check.
        // @ts-expect-error
        el = target.nodeType === NODE_TYPE.TEXT_NODE ? target.parentNode : target;
    } catch (err) {
        // COMPAT: In Firefox, `target.nodeType` will throw an error if target is
        // originating from an internal "restricted" element (e.g. a stepper
        // arrow on a number input)
        // see github.com/ianstormtaylor/slate/issues/1819
        if (IS_FIREFOX && FIREFOX_NODE_TYPE_ACCESS_ERROR.test(err.message)) {
            return false;
        }

        throw err;
    }
    return (
        (!mustBeEditable || el.isContentEditable) &&
        (el === element || findDOMClosest(el, '[data-slate-editor]') === element)
    );
}

class SlateRangeError extends Error {
    selection: SelectionRange;
    anchorAncestors: string[];
    focusAncestors: string[];

    constructor(message: string, value: Value) {
        super(message);

        const { selection, document } = value;

        this.selection = selection;
        // @ts-expect-error
        this.anchorAncestors = this.getAncestorsTypes(document, selection.anchorKey);
        // @ts-expect-error
        this.focusAncestors = this.getAncestorsTypes(document, selection.focusKey);
    }

    getAncestorsTypes(document: Document, key: string) {
        const ancestors = document.getAncestors(key);
        if (!ancestors) {
            return [];
        }
        return ancestors.toArray().map((node) => node.type);
    }
}

/*
 * Throw an async error when a native range for
 * the current slate selection could not be found in the DOM.
 */
function reportRangeError(value: Value): void {
    // For logging purpose
    const error = new SlateRangeError(
        'Unable to find a native DOM range for current selection.',
        value
    );

    reportException(error);
}
